// @ts-strict-ignore
import { communicationService } from '@ad-engine/communication';
import { PLATFORM_BEFORE_PAGE_CHANGE } from '../../communication/events/events-platform-nar';
import { getAdStack } from '../ad-engine';
import { AdSlotEvent, AdSlotStatus, type AdSlot } from '../models';
import {
	btfBlockerService,
	context,
	slotDataParamsUpdater,
	slotService,
	SlotTargeting,
	targetingService,
	trackingOptIn,
} from '../services';
import { decorate, defer, isCoppaSubject, logger } from '../utils';
import { GptSizeMap } from './gpt-size-map';
import { initGptTargeting } from './gpt-targeting';
import { Provider } from './provider';
import { utils } from '../index';
import { isHidden } from '../utils/is-hidden';
import PrivacySettingsConfig = googletag.PrivacySettingsConfig;

const logGroup = 'gpt-provider';

export const ADX = 'AdX';
export const GAMOrigins: string[] = [
	'https://tpc.googlesyndication.com',
	'https://googleads.g.doubleclick.net',
];

const noFillOnAdSlot = utils.queryString.get('no-fill-ad-slot');
const noFillStartRvVal = parseInt(utils.queryString.get('no-fill-start-rv-val'), 10);
// If this param is provided, then there will be a consistent no fill multiple times
const noFillEndRvVal = parseInt(utils.queryString.get('no-fill-end-rv-val'), 10) ?? 99;

// This means all Viewport sizes, so any screens that are greater than 0px in width and height, which means all screen sizes.
// All Ad Sizes that are configured with this ViewportSize will be served on any screen size.
// To be clear, screen size refers to the browser screen size (or more specifically ViewPortSize),
// so the window.innerWidth and window.innerHeight values.
const AllViewportSizes = [0, 0];

function postponeExecutionUntilGptLoads(method: () => void): any {
	return function (...args: any): void {
		setTimeout(() => {
			window.googletag = window.googletag || ({} as googletag.Googletag);
			window.googletag.cmd = window.googletag.cmd || [];

			return window.googletag.cmd.push(() => method.apply(this, args));
		});
	};
}

let definedSlots: googletag.Slot[] = [];
let initialized = false;

function getAdSlotFromEvent(
	event:
		| googletag.events.ImpressionViewableEvent
		| googletag.events.SlotOnloadEvent
		| googletag.events.SlotRenderEndedEvent,
): AdSlot | null {
	const id = event.slot.getSlotElementId();

	return slotService.get(id);
}

function configure(): void {
	const tag = window.googletag.pubads();

	tag.disableInitialLoad();

	tag.addEventListener('slotRequested', (event: googletag.events.SlotRequestedEvent) => {
		const adSlot = getAdSlotFromEvent(event);

		if (!adSlot) {
			return;
		}

		setLastRequestedSizes(adSlot, event);

		adSlot.clearWinningBidderDetails();
		adSlot.setStatus(AdSlotStatus.STATUS_REQUESTED);
		adSlot.emit(AdSlotEvent.SLOT_REQUESTED_EVENT);
	});

	tag.addEventListener('slotResponseReceived', (event: googletag.events.SlotOnloadEvent) => {
		const adSlot = getAdSlotFromEvent(event);

		adSlot.emit(AdSlotEvent.SLOT_RESPONSE_RECEIVED);
	});

	tag.addEventListener('slotOnload', (event: googletag.events.SlotOnloadEvent) => {
		const adSlot = getAdSlotFromEvent(event);

		adSlot?.emit(AdSlotEvent.SLOT_LOADED_EVENT);
	});

	tag.addEventListener('slotRenderEnded', (event: googletag.events.SlotRenderEndedEvent) => {
		// IE doesn't allow us to inspect GPT iframe at this point.
		// Let's launch our callback in a setTimeout instead.
		defer(() => {
			const adSlot = getAdSlotFromEvent(event);

			if (!adSlot) {
				return;
			}

			const adSlotIframe = adSlot.getIframe();
			const adType = getAdType(event, adSlotIframe);

			blockRefreshOnDirectSponsorshipIfApplicable(event, adSlotIframe);

			adjustIframeSize(adSlot);

			/*We emit the SLOT_RENDERED_EVENT only from this place as of Sept 19th, 2024.
			 *
			 * There are a lot of places that subscribe to this event, including:
			 * metric-reporter.ts
			 * slot-tracker.ts
			 * click-tracker.ts
			 *
			 * It's beneficial to keep track of what state the slot is in. This allows us to distinguish the different states the slots are in
			 * such as a collapsed state, or the new no-ad state that gets set when the icSlotRefreshOnNoAd flag is set to true.
			 * */
			return adSlot.emit(AdSlotEvent.SLOT_RENDERED_EVENT, { event, adType }, false);
		});
	});

	tag.addEventListener('impressionViewable', (event: googletag.events.ImpressionViewableEvent) => {
		const adSlot = getAdSlotFromEvent(event);

		adSlot?.emit(AdSlotEvent.SLOT_VIEWED_EVENT);
	});

	tag.addEventListener(
		'slotVisibilityChanged',
		function (event: googletag.events.SlotVisibilityChangedEvent) {
			const adSlot = getAdSlotFromEvent(event);

			if (!adSlot) {
				return;
			}

			adSlot?.emit(AdSlotEvent.SLOT_VISIBILITY_CHANGED, event);

			if (event.inViewPercentage > 50) {
				return adSlot.emit(AdSlotEvent.SLOT_BACK_TO_VIEWPORT, event);
			} else if (event.inViewPercentage < 50) {
				return adSlot.emit(AdSlotEvent.SLOT_LEFT_VIEWPORT, event);
			}
		},
	);

	window.googletag.enableServices();
}

function blockRefreshOnDirectSponsorshipIfApplicable(
	event: googletag.events.SlotRenderEndedEvent,
	iframe: HTMLIFrameElement | null,
) {
	let isIframeAccessible = false;
	const slot = getAdSlotFromEvent(event);

	try {
		isIframeAccessible = !!iframe.contentWindow.document.querySelector;
		if (isIframeAccessible && iframe.contentWindow.refreshBlocked) {
			logger(
				logGroup,
				'blockRefreshOnDirectSponsorshipIfApplicable',
				`Refresh blocked on ${slot?.getSlotName()} with lineItemId ${event?.lineItemId} on creativeId ${event?.creativeId}`,
			);
			slot.blockRefreshOnDirectSponsorship = true;
		}
	} catch (e) {
		logger(
			logGroup,
			'blockRefreshOnDirectSponsorshipIfApplicable',
			`iframe is not accessible on ${slot?.getSlotName()}`,
		);
	}
}

function getAdType(
	event: googletag.events.SlotRenderEndedEvent,
	iframe: HTMLIFrameElement | null,
): string {
	let isIframeAccessible = false;

	const slot = getAdSlotFromEvent(event);

	if (event.isEmpty) {
		// If there is no ad returned from GAM, we should check if the refresh on no-ad flag is active and if the slot is not at the refresh attempt limit.
		if (context.get('options.slotRefreshOnNoAd') && slot && !slot.isRefreshAttemptLimitReached()) {
			logger(
				logGroup,
				'Refresh on no-ad feature is active. Returning no-ad event on isEmpty slot.',
			);
			// We should return an Ad Status from here, otherwise we'll return STATUS_SUCCESS or STATUS_COLLAPSE
			// This event gets returned into the slotRenderEnded event listener function.
			// Reference - tag.addEventListener('slotRenderEnded', ...)
			return AdSlotStatus.STATUS_NO_AD;
		}
		return AdSlotStatus.STATUS_COLLAPSE;
	}

	try {
		isIframeAccessible = !!iframe.contentWindow.document.querySelector;
	} catch (e) {
		logger(logGroup, 'getAdType', 'iframe is not accessible');
	}

	if (isIframeAccessible && iframe.contentWindow.AdEngine_adType) {
		return iframe.contentWindow.AdEngine_adType;
	}

	return AdSlotStatus.STATUS_SUCCESS;
}

function adjustIframeSize(adSlot: AdSlot): void {
	const sizeMap = context.get('templates.sizeOverwritingMap');
	const iframe = adSlot.getIframe();

	if (!sizeMap || !iframe) {
		return;
	}

	const iframeSize = `${iframe.width}x${iframe.height}`;

	if (sizeMap[iframeSize]) {
		iframe.width = sizeMap[iframeSize].originalSize[0].toString();
		iframe.height = sizeMap[iframeSize].originalSize[1].toString();
	}
}

function SmLogger(targeting: SlotTargeting) {
	const isTlb =
		(targeting.pos.constructor == Array && targeting.pos[0] == 'top_leaderboard') ||
		targeting.pos == 'top_leaderboard';
	const smLoggerLoaded =
		window['smTracking'] !== undefined && window['smTracking'].recordRequestTargeting;

	if (isTlb && smLoggerLoaded) {
		window['smTracking'].recordRequestTargeting(targeting);
	}
}

function updateGptSlotTargeting(gptSlot: googletag.Slot, adSlot: AdSlot, targeting: SlotTargeting) {
	const castedRV = targeting.rv ? String(Number(targeting.rv) + 1) : '1';

	// NO AD FILL code
	const slotName = adSlot.getSlotName();
	if (slotName === noFillOnAdSlot) {
		logger(logGroup, 'No ad fill triggered for ad slot: ', slotName);
		const intParsedRv = parseInt(castedRV);
		// Check the no ad fill targeted slot's RV value and see if it's between the optionally
		// set range of RV values from the query params, to force no ad fill. If so, set the src to externaltest
		// so that GAM can't target any lineItems.
		if (intParsedRv >= noFillStartRvVal && intParsedRv <= noFillEndRvVal) {
			logger(logGroup, 'updateGptSlotTargeting', 'Forcing no ad fill for slot ', slotName);
			targetingService.set('src', 'externaltest', slotName);
			targeting.src = 'externaltest';
		} else {
			// Reset the src back to whatever its set at the context level, so that ads can have proper targeting, and no ad fills will not be forced.
			logger(logGroup, 'updateGptSlotTargeting', 'Resetting src for slot ', slotName);
			targetingService.set('src', context.get('src'), slotName);
			targeting.src = context.get('src');
		}
	}
	// NO AD FILL code

	gptSlot.updateTargetingFromMap(targeting);
	gptSlot.setTargeting('rv', castedRV);
	targetingService.set('rv', castedRV, adSlot.getSlotName());
}

export class GptProvider implements Provider {
	constructor() {
		window.googletag = window.googletag || ({} as googletag.Googletag);
		window.googletag.cmd = window.googletag.cmd || [];

		this.init();
	}

	isInitialized(): boolean {
		return initialized;
	}

	@decorate(postponeExecutionUntilGptLoads)
	init(): void {
		if (this.isInitialized()) {
			return;
		}

		initGptTargeting();
		configure();
		this.setupRestrictDataProcessing();
		this.setPPID();
		communicationService.on(PLATFORM_BEFORE_PAGE_CHANGE, () => this.updateCorrelator(), false);
		communicationService.onSlotEvent(AdSlotEvent.DESTROY_EVENT, ({ slot }) => {
			this.destroySlot(slot.getSlotName());
			slot.emit(AdSlotEvent.DESTROYED_EVENT);
		});
		initialized = true;
	}

	setupRestrictDataProcessing(): void {
		const tag = window.googletag.pubads();
		const settings: PrivacySettingsConfig = {
			restrictDataProcessing: trackingOptIn.isOptOutSale(),
		};

		if (isCoppaSubject()) {
			settings.childDirectedTreatment = true;
		}

		tag.setPrivacySettings(settings);
	}

	setPPID() {
		const ppid = targetingService.get('ppid');

		if (ppid) {
			const tag = window.googletag.pubads();
			tag.setPublisherProvidedId(ppid);
		}
	}

	@decorate(postponeExecutionUntilGptLoads)
	fillIn(adSlot: AdSlot): void {
		const adStack = getAdStack() || [];

		btfBlockerService.push(adSlot, (...args) => {
			this.fillInCallback(...args);
		});
		if (adStack.length === 0) {
			this.flush();
		}
	}

	private fillInCallback(adSlot: AdSlot): void {
		const adSlotName = adSlot.getSlotName();
		const targeting = adSlot.getTargeting();

		// NO AD FILL code
		if (adSlotName === noFillOnAdSlot) {
			if (noFillStartRvVal === 1 && Number(targeting.rv) === 1) {
				logger(
					logGroup,
					'fillInCallback',
					'Forcing no ad fill for slot ',
					adSlotName,
					' on rv === ',
					targeting.rv,
				);
				targetingService.set('src', 'externaltest', adSlotName);
				targeting.src = 'externaltest';
			}
		}
		// NO AD FILL code

		const sizeMap = new GptSizeMap(adSlot.getSizes());
		const gptSlot = this.createGptSlot(adSlot, sizeMap);

		if (!gptSlot) {
			return;
		}

		if (context.get('options.slotRefreshOnNoAd')) {
			logger(logGroup, `${adSlot.getSlotName()} - Setting setCollapseEmptyDiv(false)`);
			gptSlot.addService(window.googletag.pubads()).setCollapseEmptyDiv(false);
		} else {
			logger(logGroup, `${adSlot.getSlotName()} - Setting setCollapseEmptyDiv(true)`);
			gptSlot.addService(window.googletag.pubads()).setCollapseEmptyDiv(true);
		}

		this.applyTargetingParams(gptSlot, targeting);

		slotDataParamsUpdater.updateOnCreate(adSlot);

		window.googletag.display(adSlotName);
		definedSlots.push(gptSlot);

		if (!adSlot.isFirstCall()) {
			this.flush();
		}

		logger(logGroup, adSlotName, 'slot added');
	}

	/** @private */
	createGptSlot(adSlot: AdSlot, sizeMap: GptSizeMap): googletag.Slot | undefined {
		if (adSlot.isOutOfPage()) {
			if (adSlot.getConfigProperty('outOfPageFormat')) {
				return window.googletag.defineOutOfPageSlot(
					adSlot.getAdUnit(),
					// @ts-ignore for some reason our @types/doubleclick-gpt/index.d.ts don't have enums maybe after TS update
					window.googletag.enums.OutOfPageFormat[adSlot.getConfigProperty('outOfPageFormat')],
				);
			}

			return window.googletag.defineOutOfPageSlot(adSlot.getAdUnit(), adSlot.getSlotName());
		}

		return window.googletag
			.defineSlot(adSlot.getAdUnit(), adSlot.getDefaultSizes(), adSlot.getSlotName())
			?.defineSizeMapping(sizeMap.build());
	}

	applyTargetingParams(gptSlot: googletag.Slot, targeting: SlotTargeting): void {
		SmLogger(targeting);

		Object.keys(targeting).forEach((key) => {
			let value = targeting[key];

			value = Array.isArray(value) ? value.map((item) => item.toString()) : value.toString();

			gptSlot.setTargeting(key, value);
		});
	}

	@decorate(postponeExecutionUntilGptLoads)
	updateCorrelator(): void {
		window.googletag.pubads().updateCorrelator();
	}

	private flush(): void {
		if (definedSlots.length) {
			window.googletag.pubads().refresh(definedSlots, { changeCorrelator: false });
			definedSlots = [];
		}
	}

	private destroySlot(slotName: string): boolean {
		const allSlots = window.googletag.pubads().getSlots();
		const slotToDestroy = allSlots.find((gptSlot) => slotName === gptSlot.getSlotElementId());

		if (slotToDestroy) {
			this.destroyGptSlot(slotToDestroy);

			return true;
		}

		logger(logGroup, 'destroySlot', "slot doesn't return element id", slotName);

		return false;
	}

	@decorate(postponeExecutionUntilGptLoads)
	private destroyGptSlot(gptSlot: googletag.Slot): void {
		logger(logGroup, 'destroySlot', gptSlot);

		const success = window.googletag.destroySlots([gptSlot]);

		if (!success) {
			logger(logGroup, 'destroySlot', gptSlot, 'failed');
		}
	}

	static buildSizeMappingOnRefresh(adSlot: AdSlot): googletag.SizeMappingArray {
		const sizeMapping = window.googletag.sizeMapping();

		/* A platform specific flag that can be used to switch between the two methods of refreshing ad slots.
		 * If this is turned on, then there will be no additional filtering based on current slot height, since
		 * that already occurs in the slot-refresher code.
		 * In addition to that, the sizes key with predefined Viewports will be used for refreshing.
		 * If that is not available, then the previous method of using defaultSizes with a 0,0 viewportSize will be used.
		 * Both methods get rid of the extra size filtering step.
		 *
		 * This flag will be used as a gradual rollout method to ensure that refresh doesn't break on all platforms.
		 * It also allows for a quick revert of the feature, without having to do a code deployment. */
		if (context.get('options.useAdSizesOnRefresh')) {
			const adSlotSizes = adSlot.getSizes();
			const refreshSizeMapping =
				adSlotSizes?.length > 0
					? adSlotSizes
					: [
							{
								viewportSize: AllViewportSizes,
								sizes: adSlot.getDefaultSizes(),
							},
						];
			refreshSizeMapping.forEach((size) => {
				sizeMapping.addSize(size.viewportSize, size.sizes);
			});
		} else {
			// Leave this in as an option, in case something goes sideways on a certain platform.
			// We can revert back to using this, by switching off the icUseAdSizesOnRefresh ICBM variable,
			// without doing a code revert and redeployment.
			adSlot.getDefaultSizes().forEach((adSize) => {
				if (adSize[1] <= adSlot.getElement().clientHeight) {
					sizeMapping.addSize(AllViewportSizes, adSize);
				}
			});
		}

		return sizeMapping.build();
	}

	static refreshSlot(adSlot: AdSlot): void {
		if (isHidden(adSlot.getElement()) && adSlot.getConfigProperty('stopRefreshOnHidden')) {
			logger(logGroup, `${adSlot.getSlotName()} slot is hidden - refresh stopped`);
			return;
		}

		const activeSlots = window.googletag.pubads().getSlots();
		const gptSlot = activeSlots.find((slot) => slot.getSlotElementId() === adSlot.getSlotName());
		if (!gptSlot) {
			/* This issue has been occurring with the interstitial slot on ucp-mobile */
			logger(
				logGroup,
				'refreshSlot',
				'GptSlot not found. Aborting slot refresh.',
				adSlot.getSlotName(),
			);
			return;
		}
		const { targeting } = adSlot;
		gptSlot.clearTargeting();

		gptSlot.defineSizeMapping(GptProvider.buildSizeMappingOnRefresh(adSlot));
		updateGptSlotTargeting(gptSlot, adSlot, targeting);

		window.googletag.pubads().refresh([gptSlot]);
	}

	/**
	 * We need to make sure that the GPT library is loaded before we call the setConfig method
	 * or error will be thrown.
	 */
	@decorate(postponeExecutionUntilGptLoads)
	static setConfig(config: unknown): void {
		// @ts-ignore setConfig is not defined in the @types/doubleclick-gpt
		window.googletag.setConfig(config);
	}
}

function setLastRequestedSizes(adSlot: AdSlot, event: googletag.events.SlotRequestedEvent): void {
	const getPrevIuSzsParameter = (url) => {
		if (!url) {
			return null;
		}

		const regex = new RegExp(`[?&]prev_iu_szs=([^&#]*)`);
		const match = url.match(regex);
		return match ? decodeURIComponent(match[1]) : null;
	};

	try {
		const url = (event?.slot as any)?.getContentUrl?.();
		const sizes = getPrevIuSzsParameter(url) || '';
		context.set(`slots.${adSlot.getSlotName()}.lastRequestedSizes`, sizes.replaceAll('|', ','));
	} catch {
		context.set(`slots.${adSlot.getSlotName()}.lastRequestedSizes`, '');
	}
}
